import { useState } from "react";
import { Link } from "react-router";

import _, { capitalize } from "lodash";
import { SUPER_LIKE_ROLES } from "src/constants/permissions";
import {
  formatDateString,
  formatMoneyString,
} from "src/utility/utilityFunctions";

import { insertIf, showFirst } from "@components/SmartTable";
import SmartTable, {
  PaginatedResourceProps,
} from "@components/SmartTable/SmartTable";
import { showFirstN } from "@components/SmartTable/helpers";
import ItemPreviewImageButton from "@features/items/components/ItemPreviewImageButton";
import { PaperTooltip } from "@features/ui";
import { OrderOrigin } from "@models/Order";
import { OrderVariant } from "@models/OrderVariant";
import { Variant } from "@models/Variant";
import { formatLocalDate } from "@utils/date";
import useRoleIs from "@utils/useRoleIs";

import {
  BillableBeaconCost,
  BillableItemCost,
  BillableShippingCost,
  BillableTaxCost,
} from "../BillableCosts";
import {
  getExtendedCost,
  getVariantName,
  handleComplianceStatus,
  orderTypeLabel,
} from "../helpers";
import OrderVariantTrackingModal from "./OrderVariantTrackingModal";

type OrderHistoryVariantTableProps = PaginatedResourceProps & {
  data: OrderVariant[];
  tableRef: React.MutableRefObject<any>;
};

const OrderHistoryVariantTable = ({
  data,
  ...props
}: OrderHistoryVariantTableProps) => {
  const [trackingVariant, setTrackingVariant] = useState<null | OrderVariant>(
    null
  );
  const roleIs = useRoleIs();

  return (
    <>
      {trackingVariant && (
        <OrderVariantTrackingModal
          onClose={() => setTrackingVariant(null)}
          orderVariant={trackingVariant}
        />
      )}
      <SmartTable
        rows={data}
        {...props}
        rowCss={(ov) =>
          ov.status === "canceled" && "bg-red-50 hover:bg-red-100"
        }
        columns={[
          {
            id: "_preview",
            label: "Preview",
            render: (_, { variant }) => (
              <ItemPreviewImageButton item={variant.item} variant={variant} />
            ),
          },
          {
            id: "variant",
            label: "Item",
            sort: "item-number",
            reverseSort: true,
            render: (variant: Variant) => (
              <div>
                <div className="text-neutral-600 font-medium tracking-wide">
                  {variant.variantSku}
                </div>

                {variant.item.comment}
                {variant.selectedVariantOptions.length > 0 && (
                  <div className="text-sm text-neutral-500">
                    {getVariantName(variant)}
                  </div>
                )}
              </div>
            ),
          },
          {
            id: "_typeOrWindow",
            label: "Order Type / Window",
            render: (__, ov) =>
              ov.orderOrderingType === "pre-order"
                ? (ov.orderWindowName ?? orderTypeLabel(ov.orderOrderingType))
                : orderTypeLabel(ov.orderOrderingType),
          },
          {
            id: "_inMarketMonth",
            label: "In Market Month",
            render: (_, ov) =>
              formatLocalDate(
                ov.poInMarketDate ?? ov.inMarketDate,
                "MMMM yyyy"
              ) || <span className="text-neutral-400">Pending</span>,
          },
          {
            id: "isForDistributor",
            label: "Origin",
            render: (v) => (v ? OrderOrigin.Distributor : OrderOrigin.Internal),
          },
          {
            id: "order.id",
            sort: "id", // translated to "order-id"
            reverseSort: true,
            label: "Order #",
            render: (id) => (
              <Link
                to={`/orders/history/single-order/${id}`}
                className="underline font-medium tracking-wide"
              >
                {id}
              </Link>
            ),
          },
          {
            id: "anaplanSubProgramCodes",
            label: "12 Digit Code(s)",
            render: (codes) => showFirstN(codes, 3),
          },
          {
            id: "anaplanProgramTypes",
            label: "Anaplan Program Types",
            render: (types) => types.join(", "),
          },
          ...insertIf(
            roleIs([
              ...SUPER_LIKE_ROLES,
              "purchaser",
              "select-purchaser",
              "read-only",
              "compliance",
              "field2",
            ]),
            { id: "poId", label: "PO #" }
          ),
          { id: "orderUserName", label: "Ordered By" },
          {
            id: "variant.item.brands",
            label: "Brand",
            sort: "brand",
            render: (brands) => showFirst(brands.map((b) => b.name)),
          },
          {
            id: "_businessUnitNames",
            label: "BU",
            render: (__, ov) =>
              _(ov.variant.item.brands)
                .map("businessUnit.name")
                .uniq()
                .join(", "),
          },
          { id: "programNames", label: "Program", render: showFirst },
          {
            id: "itemTypeDescription",
            label: "Item Type",
            sort: "item-type-description",
          },
          {
            id: "addressName",
            label: "Distributor / Name",
            sort: "address-name",
          },
          { id: "addressAbn", label: "ABN" },
          { id: "state", label: "State" },
          { id: "territoryName", label: "Territory" },
          { id: "qty", label: "Qty" },
          {
            id: "estimatedCost",
            label: "Est. Cost/Unit",
            align: "right",
            render: (v) => formatMoneyString(v),
          },
          {
            id: "_perUnitActualCost",
            label: "Actual Cost/Unit",
            align: "right",
            render: (_, ov) =>
              formatMoneyString(+ov.totalActualCost / (ov.qty || 1)),
          },
          {
            id: "_totalProductCost",
            label: "Total Product Cost",
            align: "right",
            render: (_, ov) => <BillableItemCost costs={ov} />,
          },
          {
            id: "totalEstimatedShippingCost",
            label: "Est. Freight",
            align: "right",
            render: (_, ov) => <BillableShippingCost costs={ov} />,
          },
          {
            id: "totalEstimatedTax",
            label: "Est. Tax",
            align: "right",
            render: (_, ov) => <BillableTaxCost costs={ov} />,
          },
          {
            id: "totalBeaconCost",
            label: "Total Beacon Cost",
            align: "right",
            render: (_, ov) => <BillableBeaconCost costs={ov} />,
          },
          {
            id: "_extendedTotal",
            label: "Est. Total Cost",
            align: "right",
            render: (_, ov) => formatMoneyString(getExtendedCost(ov)),
          },
          {
            id: "orderSubmittedAt",
            label: "Order Date",
            sort: "submitted-at", // translated to "order-submitted-at"
            render: (date) => formatLocalDate(date),
          },
          {
            id: "_shipDateOrInMarketDate",
            label: "In Market Date",
            sort: "in-market-date",
            reverseSort: true,
            render: (_, ov) =>
              formatDateString(ov.poInMarketDate ?? ov.inMarketDate),
          },
          {
            id: "_actualShipDate",
            label: "Act. Ship Date",
            sort: "shipped-at", // translated to "order-shipped-at"
            reverseSort: true,
            render: (_, ov) =>
              formatDateString(
                ov.shippingParameterVariant?.actualShipDate ?? ov.actualShipDate
              ),
          },
          {
            id: "trackingData",
            label: "Tracking",
            render: (d: OrderVariant["trackingData"], ov) =>
              ov.isTransferred
                ? "Transferred qtys from other territories"
                : d && (
                    <div>
                      <div className="uppercase tracking-wide">{d.carrier}</div>
                      <button
                        onClick={() => setTrackingVariant(ov)}
                        className="underline text-left"
                      >
                        {d.number}
                      </button>
                    </div>
                  ),
          },
          {
            id: "_status",
            label: "Status",
            render: (_s, ov) =>
              ov.status === "canceled" ? (
                <div className="text-red-900 whitespace-nowrap">
                  <span>
                    {ov.cancelationType === "compliance"
                      ? "Compliance Canceled"
                      : "Canceled"}
                  </span>
                  {ov.cancelationNote && (
                    <PaperTooltip title={ov.cancelationNote}>
                      <span className="underline pl-2">(note)</span>
                    </PaperTooltip>
                  )}
                </div>
              ) : (
                _.capitalize(ov.orderStatus)
              ),
          },
          {
            id: "_compliance",
            label: "Compliance Status",
            render: (_, ov) => {
              const rules = [
                ...ov.triggeredRules,
                ...ov.priorApprovalTriggeredRules,
              ];
              if (rules.length === 0) {
                return <span>Ok</span>;
              }
              return (
                <span>
                  {ov.overridesCompliance
                    ? `Overridden (${rules
                        .map((ruleObj) => capitalize(ruleObj.ruleType))
                        .join(", ")}) `
                    : handleComplianceStatus(rules)}
                </span>
              );
            },
          },
          {
            id: "variant.item.includeBeacon",
            label: "Beacon",
            render: (includeBeacon) => (includeBeacon ? "Yes" : "No"),
          },
          { id: "approvalLogging", label: "Approval Logs" },
        ]}
      />
    </>
  );
};

export default OrderHistoryVariantTable;
